import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useSystem } from '@ohif/core';
import { expandDropped, IntakeError } from '../utils/dicomIntake';
import { UploadRow, UploadStatus } from '../utils/uploadTypes';
import { runUploadBatch, rollbackStudies } from '../utils/uploadBatch';
import { describeError } from '../utils';

type Phase = 'idle' | 'intaking' | 'uploading' | 'halted' | 'finished';

interface State {
  phase: Phase;
  rows: UploadRow[];
  activeIndex: number;
  haltMessage: string | null;
  canForce: boolean;
  isResolving: boolean;
}

type Action =
  | { type: 'intakeStarted' }
  | { type: 'intakeFailed' }
  | { type: 'filesReady'; rows: UploadRow[] }
  | { type: 'batchStarted' }
  | { type: 'fileStarted'; index: number }
  | { type: 'fileProgress'; index: number; progress: number }
  | { type: 'fileSettled'; index: number; status: UploadStatus; error?: string }
  | { type: 'halted'; message: string; canForce: boolean }
  | { type: 'batchFinished' }
  | { type: 'rollbackStarted' }
  | { type: 'reset' };

const INITIAL: State = {
  phase: 'idle',
  rows: [],
  activeIndex: -1,
  haltMessage: null,
  canForce: false,
  isResolving: false,
};

function replaceRow(rows: UploadRow[], index: number, patch: Partial<UploadRow>): UploadRow[] {
  const next = rows.slice();
  next[index] = { ...next[index], ...patch };
  return next;
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'intakeStarted':
      return { ...INITIAL, phase: 'intaking' };

    case 'intakeFailed':
    case 'reset':
      return INITIAL;

    case 'filesReady':
      return { ...INITIAL, phase: 'uploading', rows: action.rows };

    case 'batchStarted':
      return {
        ...state,
        phase: 'uploading',
        haltMessage: null,
        canForce: false,
        isResolving: false,
      };

    case 'fileStarted':
      return {
        ...state,
        activeIndex: action.index,
        rows: replaceRow(state.rows, action.index, {
          status: UploadStatus.InProgress,
          progress: 0,
          error: undefined,
        }),
      };

    case 'fileProgress': {
      const row = state.rows[action.index];

      return row?.status === UploadStatus.InProgress && row.progress !== action.progress
        ? { ...state, rows: replaceRow(state.rows, action.index, { progress: action.progress }) }
        : state;
    }

    case 'fileSettled':
      return {
        ...state,
        activeIndex: -1,
        rows: replaceRow(state.rows, action.index, {
          status: action.status,
          error: action.error,
          progress:
            action.status === UploadStatus.Success ? 100 : (state.rows[action.index]?.progress ?? 0),
        }),
      };

    case 'halted':
      return {
        ...state,
        phase: 'halted',
        activeIndex: -1,
        haltMessage: action.message,
        canForce: action.canForce,
      };

    case 'batchFinished':
      return { ...state, phase: 'finished', activeIndex: -1 };

    case 'rollbackStarted':
      return { ...state, isResolving: true };

    default:
      return state;
  }
}

export interface UploadStats {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  totalProgress: number;
}

interface UseDicomUploadOptions {
  onComplete: () => void;
  onStarted: () => void;
}

export function useDicomUpload({ onComplete, onStarted }: UseDicomUploadOptions) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;

  const [state, dispatch] = useReducer(reducer, INITIAL);

  const filesRef = useRef<File[]>([]);

  // The overlay masks the drop target, but a mask is presentation — the
  // one-drop-one-study invariant is enforced here so it survives a restyle.
  const isIntakingRef = useRef(false);

  const hasStartedRef = useRef(false);

  const studyUidsRef = useRef<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  const isRunningRef = useRef(false);

  const uploadedCountRef = useRef(0);

  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
      abortRef.current?.abort();
    };
  }, []);

  const runFrom = useCallback(
    async (indices: number[], force: boolean) => {
      if (isUnmountedRef.current || isRunningRef.current) {
        return;
      }
      isRunningRef.current = true;

      try {
        const controller = new AbortController();
        abortRef.current = controller;

        dispatch({ type: 'batchStarted' });

        const outcome = await runUploadBatch(filesRef.current, indices, {
          force,
          signal: controller.signal,
          studyUids: studyUidsRef.current,
          callbacks: {
            onFileStarted: (index: number) => dispatch({ type: 'fileStarted', index }),
            onFileProgress: (index: number, progress: number) =>
              dispatch({ type: 'fileProgress', index, progress }),
            onFileSettled: (index: number, status: UploadStatus, error?: string) => {
              if (status === UploadStatus.Success) {
                uploadedCountRef.current += 1;
              }

              dispatch({ type: 'fileSettled', index, status, error });
            },
          },
        });

        switch (outcome.type) {
          case 'completed':
            dispatch({ type: 'batchFinished' });
            onComplete();
            return;

          case 'cancelled':
            dispatch({ type: 'batchFinished' });

            if (uploadedCountRef.current > 0) {
              uiNotificationService?.show({
                title: 'Upload',
                message: `Upload cancelled. ${uploadedCountRef.current} file(s) were already uploaded and remain on the server.`,
                type: 'warning',
                duration: 5000,
              });
            }

            onComplete();
            return;

          case 'rejected':
            dispatch({ type: 'halted', message: outcome.message, canForce: true });
            return;

          case 'failed':
            dispatch({ type: 'halted', message: outcome.message, canForce: false });
            return;

          case 'unauthorized':
            dispatch({ type: 'batchFinished' });
            return;

          default: {
            const unhandled: never = outcome;
            throw new Error(`Unhandled upload outcome: ${JSON.stringify(unhandled)}`);
          }
        }
      } catch (error) {
        console.error(`[sycai] upload run failed ${describeError(error)}`);

        dispatch({
          type: 'halted',
          message: error instanceof Error ? error.message : 'Upload failed',
          canForce: false,
        });
      } finally {
        abortRef.current = null;
        isRunningRef.current = false;
      }
    },
    [onComplete, uiNotificationService]
  );

  const handleFiles = useCallback(
    async (dropped: File[]) => {
      if (isUnmountedRef.current) {
        return;
      }

      if (isIntakingRef.current || dropped.length === 0) {
        return;
      }

      if (studyUidsRef.current.size > 0) {
        return;
      }

      isIntakingRef.current = true;
      dispatch({ type: 'intakeStarted' });

      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        onStarted();
      }

      try {
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

        const { files, unreadable } = await expandDropped(dropped);

        if (unreadable > 0) {
          uiNotificationService?.show({
            title: 'Upload',
            message: `Skipped ${unreadable} file(s) using an unsupported compression method.`,
            type: 'warning',
            duration: 5000,
          });
        }

        if (files.length === 0) {
          dispatch({ type: 'intakeFailed' });

          uiNotificationService?.show({
            title: 'Upload',
            message: 'No DICOM files found in the drop.',
            type: 'error',
            duration: 5000,
          });
          return;
        }

        filesRef.current = files;
        studyUidsRef.current = new Set();
        uploadedCountRef.current = 0;
        abortRef.current = null;

        dispatch({
          type: 'filesReady',
          rows: files.map(file => ({
            name: file.name,
            size: file.size,
            status: UploadStatus.NotStarted,
            progress: 0,
          })),
        });

        await runFrom(
          files.map((unused: File, index: number) => index),
          false
        );
      } catch (error) {
        dispatch({ type: 'intakeFailed' });

        uiNotificationService?.show({
          title: 'Upload',
          message:
            error instanceof IntakeError ? error.message : 'Could not read the dropped files',
          type: 'error',
          duration: 5000,
        });

        if (!(error instanceof IntakeError)) {
          console.error(`[sycai] intake failed ${describeError(error)}`);
        }
      } finally {
        isIntakingRef.current = false;
      }
    },
    [onStarted, runFrom, uiNotificationService]
  );

  const continueWithForce = useCallback(async () => {
    const indices = state.rows
      .map((unused: UploadRow, index: number) => index)
      .filter(index => state.rows[index].status !== UploadStatus.Success);

    await runFrom(indices, true);
  }, [state.rows, runFrom]);

  const isStoppingRef = useRef(false);

  const stopAndRollback = useCallback(async () => {
    if (isStoppingRef.current) {
      return;
    }
    isStoppingRef.current = true;

    try {
      dispatch({ type: 'rollbackStarted' });

      const failed = await rollbackStudies(studyUidsRef.current);

      studyUidsRef.current = new Set();
      filesRef.current = [];
      uploadedCountRef.current = 0;
      hasStartedRef.current = false;

      dispatch({ type: 'reset' });

      if (failed.length > 0) {
        uiNotificationService?.show({
          title: 'Upload',
          message: `Could not remove ${failed.length} uploaded stud${failed.length === 1 ? 'y' : 'ies'} from the server.`,
          type: 'error',
          duration: 5000,
        });
      }
    } finally {
      isStoppingRef.current = false;
    }
  }, [uiNotificationService]);

  const cancelAll = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const stats = useMemo<UploadStats>(() => {
    const { rows } = state;
    const completedFiles = rows.filter(row => row.status === UploadStatus.Success).length;
    const failedFiles = rows.filter(row => row.status === UploadStatus.Failed).length;

    return {
      totalFiles: rows.length,
      completedFiles,
      failedFiles,
      totalProgress: rows.length
        ? rows.reduce((sum, row) => sum + row.progress, 0) / rows.length
        : 0,
    };
  }, [state]);

  return {
    phase: state.phase,
    rows: state.rows,
    activeIndex: state.activeIndex,
    haltMessage: state.haltMessage,
    canForce: state.canForce,
    isResolving: state.isResolving,
    stats,
    handleFiles,
    continueWithForce,
    stopAndRollback,
    cancelAll,
  };
}
